import { useRouter } from 'next/router'
import { useTheme } from '@cellectenergy/cellect-react-components'
import { useTranslation } from 'react-i18next'
import { ParsedUrlQuery } from 'querystring'

// Domain
import { Topic } from '@/domains/topic'

// Components
import { Box, Chip, Typography } from '@mui/material'
import { BlogCategoryChip } from '@/storyblokComponents/legacy/BlogCategoryChip'

// Styles
import * as Styled from './Topics.styles'
import { TopicsFilter } from './Topics.styles'

const Topics = ({ list }: { list: Topic[] }) => {
  const { t } = useTranslation('global')
  const { push, query } = useRouter()
  const theme = useTheme()

  const handleClick = (ev: string) => {
    if (queryHadNoTopic(query, ev)) {
      push({ query: { ...query, topic: ev } }, undefined, {
        shallow: true,
      })
      return
    }

    if (selectedTopicIsAlreadyInUseOrAll(query, ev)) {
      const newQuery = query
      delete newQuery.topic
      push({ query: newQuery }, undefined, {
        shallow: true,
      })
      return
    }

    if (newTopicSelected(query, ev)) {
      const newTopic: string = query.topic as string
      push({ query: { ...query, topic: [newTopic, ev] } }, undefined, {
        shallow: true,
      })
      return
    }

    if (selectedTopicWasAlreadyInUse(query)) {
      const topics: string[] = query.topic as string[]
      const selectedTopic = topics.find((t: string) => t === ev)
      const newQuery = {
        ...query,
        topic: selectedTopic === undefined ? [...topics, ev] : topics.filter((t: string) => t !== ev),
      }
      push({ query: newQuery }, undefined, {
        shallow: true,
      })
      return
    }
  }

  return (
    <Styled.TopicsContainer>
      <Typography color={theme.palette.common.corporateColors.primary.white} variant="label-large">
        {t(`Blog.topicsLabel`)}
      </Typography>
      <TopicsFilter>
        <Box
          sx={{
            display: 'flex',
            gap: '0.62rem',
            flex: '1 0 0',
          }}
        >
          <Chip
            sx={() => ({
              height: '24px',
              color: !query.topic
                ? theme.palette.common.corporateColors.primary.midNightBlue
                : theme.palette.common.corporateColors.primary.white,
              background: !query.topic
                ? theme.palette.common.corporateColors.secondary.turquoise
                : theme.palette.common.corporateColors.primary.midNightBlue,
              border: `1px solid`,
              borderColor: `${
                !query.topic
                  ? theme.palette.common.corporateColors.secondary.turquoise
                  : theme.palette.common.corporateColors.primary.white
              }`,
            })}
            label={<Typography className="label-medium">{t(`Blog.all`)}</Typography>}
            onClick={() => handleClick('all')}
          />
          {list && list.length > 0
            ? list.map((topic, index) => (
                <BlogCategoryChip
                  selected={isTopicSelected(query, topic.attributes.name)}
                  key={index + topic.attributes.name}
                  category={topic.attributes.name}
                  onClick={() => handleClick(topic.attributes.name)}
                />
              ))
            : null}
        </Box>
      </TopicsFilter>
    </Styled.TopicsContainer>
  )
}

export default Topics
function selectedTopicWasAlreadyInUse(query: ParsedUrlQuery) {
  return query.topic && typeof query.topic !== 'string'
}

function newTopicSelected(query: ParsedUrlQuery, ev: string) {
  return typeof query.topic === 'string' && query.topic !== ev
}

function selectedTopicIsAlreadyInUseOrAll(query: ParsedUrlQuery, ev: string) {
  return (typeof query.topic === 'string' && query.topic === ev) || ev === 'all'
}

function queryHadNoTopic(query: ParsedUrlQuery, ev: string) {
  return !query.topic && ev !== 'all'
}

function isTopicSelected(query: ParsedUrlQuery, ev: string) {
  if (typeof query.topic === 'string') {
    return query.topic === ev
  }
  if (typeof query.topic === 'object') {
    return query.topic.includes(ev)
  }
  return false
}
